import { FC, useState } from 'react'
import { Controller } from 'react-hook-form'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'

import GridItem, { GridItemProps } from 'common-ui/components/GridItem'
import { validate } from '../../../utils'

type BitrateUnit = 'bps' | 'kbps' | 'Mbps'

const UNIT_MULTIPLIERS: Record<BitrateUnit, number> = {
  bps: 1,
  kbps: 1000,
  Mbps: 1000000,
}

const bpsToDisplay = (bps: number | '' | undefined | null, unit: BitrateUnit): string => {
  if (bps === undefined || bps === null || bps === '') return ''
  if (typeof bps !== 'number' || isNaN(bps)) return ''
  const value = bps / UNIT_MULTIPLIERS[unit]
  // Round to avoid floating point issues (up to 6 decimal places)
  return String(Math.round(value * 1000000) / 1000000)
}

const displayToBps = (value: string, unit: BitrateUnit): number | '' => {
  if (value === '' || value === undefined) return ''
  const numValue = parseFloat(value)
  if (isNaN(numValue)) return ''
  return Math.round(numValue * UNIT_MULTIPLIERS[unit])
}

interface BitrateInputProps extends GridItemProps {
  name: string
  label: string
  required?: boolean
  disabled?: boolean
  defaultUnit?: BitrateUnit
}

/**
 * Bitrate input with unit selector (bps, kbps, Mbps).
 * The form stores the value in bps, but the UI displays in the selected unit.
 */
const BitrateInput: FC<BitrateInputProps> = ({
  name,
  label,
  required,
  disabled,
  defaultUnit = 'Mbps',
  ...gridProps
}) => {
  const [unit, setUnit] = useState<BitrateUnit>(defaultUnit)

  const validators: Parameters<typeof validate>[0] = {}
  if (required) {
    validators.presence = true
  }
  validators.number = {
    greaterThanOrEqualTo: 0,
    message: 'Cannot be negative',
  }

  return (
    <GridItem {...gridProps}>
      <Controller
        name={name}
        rules={{
          validate: validate(validators),
        }}
        render={({ field: { ref, value, onChange, ...field }, fieldState }) => {
          const showError = fieldState.invalid
          const fieldError = fieldState.error ? fieldState.error.message : ''

          // Convert stored bps to display value
          const displayValue = bpsToDisplay(value, unit)

          const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value
            const bpsValue = displayToBps(inputValue, unit)
            onChange(bpsValue)
          }

          const handleUnitChange = (newUnit: BitrateUnit) => {
            setUnit(newUnit)
            // Value stays the same in bps, just the display unit changes
          }

          return (
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <TextField
                {...field}
                inputRef={ref}
                value={displayValue}
                onChange={handleValueChange}
                label={label}
                type="number"
                error={showError}
                helperText={showError ? fieldError : undefined}
                fullWidth
                variant="outlined"
                margin="normal"
                disabled={disabled}
                slotProps={{
                  inputLabel: {
                    shrink: displayValue !== '' || undefined,
                  },
                  htmlInput: {
                    min: 0,
                    step: 'any',
                  },
                }}
                onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                data-test-id={`bitrateinput-${name}`}
                sx={{ flex: 1 }}
              />
              <FormControl variant="outlined" margin="normal" sx={{ minWidth: 90 }} disabled={disabled}>
                <Select
                  value={unit}
                  onChange={(e) => handleUnitChange(e.target.value as BitrateUnit)}
                  data-test-id={`bitrateinput-${name}-unit`}
                >
                  <MenuItem value="bps">bps</MenuItem>
                  <MenuItem value="kbps">kbps</MenuItem>
                  <MenuItem value="Mbps">Mbps</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )
        }}
      />
    </GridItem>
  )
}

export default BitrateInput
